
import axiosInstance from "./Axios";

import { handleError } from "../Helpers/ErrorHandler";


export interface PaymentPlanSummary {
    paymentPlanId: number;
    creditId: number;
    snoozeAvailable: boolean;
    snoozableAmount: string;
    snoozeFee: string;
    dueDate: string;
    shopName: string;
    shopLogoUrl: string;
    diffDays?: number;
    /** Client-only: selection in bulk UI; not from API. */
    selected?: boolean;
}

function parseAmountString(value: string): number {
    const n = parseFloat(String(value).replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(n) ? n : 0;
}

/** Aggregates per-plan fields from `PaymentPlanSummary` rows (fallback when bulk totals are absent). */
export function aggregatePaymentPlanSummaries(plans: PaymentPlanSummary[]): {
    count: number;
    totalSnoozableAmount: number;
    totalSnoozeFee: number;
} {
    return plans.reduce(
        (acc, p) => ({
            count: acc.count + 1,
            totalSnoozableAmount:
                acc.totalSnoozableAmount + parseAmountString(String(p.snoozableAmount)),
            totalSnoozeFee: acc.totalSnoozeFee + parseAmountString(String(p.snoozeFee)),
        }),
        { count: 0, totalSnoozableAmount: 0, totalSnoozeFee: 0 }
    );
}

export interface BulkSnoozePreviewRequest {
    snoozeDate: string;
    paymentPlanIds: number[];
}

export interface BulkSnoozePreviewResponse {
    previewId: string;
    paymentPlans: PaymentPlanSummary[];
    totalAmount: number;
    totalSnoozeFee: number;
    calculatedPaymentPlanCount?: number;
}

/** Strip UI-only fields for a stable catalog snapshot (first `paymentPlans` from API). */
export function stripPaymentPlanSummaryClientFields(
    plans: PaymentPlanSummary[]
): PaymentPlanSummary[] {
    return plans.map((p) => {
        const copy: PaymentPlanSummary = { ...p };
        delete copy.selected;
        return copy;
    });
}

/**
 * UI list always comes from `catalog` (first full `paymentPlans`). API may return a subset;
 * we only take totals/metadata from `apiPreview` and set `selected` from `selectedPaymentPlanIds`.
 */
export function buildBulkSnoozePreviewFromCatalogAndSelection(
    catalog: PaymentPlanSummary[],
    apiPreview: BulkSnoozePreviewResponse,
    selectedPaymentPlanIds: number[]
): BulkSnoozePreviewResponse {
    const selectedSet = new Set(selectedPaymentPlanIds);
    return {
        ...apiPreview,
        paymentPlans: catalog.map((p) => ({
            ...p,
            selected: selectedSet.has(p.paymentPlanId),
        })),
        calculatedPaymentPlanCount: selectedPaymentPlanIds.length,
    };
}

export interface BulkSnoozeDatesResponse {
    isSnoozeAvailable: boolean;
    startDate: string;
    endDate: string;
}

export const getBulkSnoozeDates = async (): Promise<BulkSnoozeDatesResponse | undefined> => {


    try {
        const response = await axiosInstance.get<BulkSnoozeDatesResponse>('/snooze/availability');
        return response.data;
    } catch (error) {
        handleError(error);
    }
}

export const previewSnooze = async (body: BulkSnoozePreviewRequest): Promise<BulkSnoozePreviewResponse | undefined> => {

    try {
        const data = await axiosInstance.post<BulkSnoozePreviewResponse>(
            `/snooze/bulk-preview`, body,
        );
        return data.data;
    } catch (error) {
        handleError(error);
    }
};
